import { MenuButton } from "@msys/ui";
import { Divider, IconButton, Stack, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { AskWhom, ItemType } from "../../../clients/graphqlTypes.js";
import FileMoveIcon from "../../assets/icons/icon-file-move.svg?react";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { ViewModeEditMenuItem } from "../../commons/ViewModeEditMenuItem.js";
import { ViewMode } from "../../commons/ViewModeMenuItem.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { useProjectQuoteContext } from "../../main-routes/projects/ProjectQuoteContext.js";
import {
  itemCanBeReplacedWithTemplates,
  itemCanHaveVerticalSelection,
} from "../doc-items/app-conditions/actions.js";
import { rootItemsAllowedConstraints } from "../doc-items/constraints.js";
import { PropertiesDebugMenuItem } from "../doc-items/debug/PropertiesDebug.js";
import { RunAttributeExpressionsMenuItem } from "../doc-items/debug/RunAttributeExpressionsMenuItem.js";
import { DocItemContextMenuItems } from "../doc-items/menus/DocItemContextMenuItems.js";
import { ItemVerticalMenuItem } from "../doc-items/menus/ItemVerticalMenuItem.js";
import { OutsourceMenuItem } from "../doc-items/menus/OutsourceMenuItem.js";
import { ProjectBuildingPlacementMenuItem } from "../doc-items/menus/ProjectBuildingPlacementMenuItem.js";
import { ReplaceWithTemplatesMenuItem } from "../doc-items/menus/ReplaceWithTemplatesMenuItem.js";
import { ExportAsXiopdMenuItem } from "../importExport/xiopdExport/ExportAsXiopdMenuItem.js";
import { RefinementWizardButton } from "../projects/quote-trees.js";
import { QuoteTemplateSaveFromItemMenuItem } from "../templates/quote/buttons/QuoteTemplateSaveFromItemMenuItem.js";
import { QuoteTemplateUpdateFromItemMenuItem } from "../templates/quote/buttons/QuoteTemplateUpdateFromItemMenuItem.js";
import { CreateProductListFromSectionMenuItem } from "./CreateProductListFromSectionMenuItem.js";
import { isRefinementAskWhenContext } from "./isRefinementAskWhenContext.js";
import {
  QuoteItemActions_ProjectFragment,
  QuoteItemActions_QuoteFragment,
  QuoteItemActions_QuoteItemFragment,
} from "./QuoteItemActions.generated.js";
import { ItemAction, QuoteItemMenuItems } from "./QuoteItemMenuItems.js";
import { getSubcontractPath } from "./helpers.js";
import { RestrictedByProjectPermissionWithDebug } from "../../auth/RestrictedByProjectPermission.js";

interface Props {
  canEdit: boolean;
  isManualSave: boolean;
  project: QuoteItemActions_ProjectFragment;
  quote: QuoteItemActions_QuoteFragment;
  item: QuoteItemActions_QuoteItemFragment;
  pathToDocPage: string;
  onSetContentRefetchQueries: string[];
  viewMode: ViewMode;
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
  documentItemTypes: ItemType[];
  expandedItemIds: string[] | undefined;
  pasteItem: ((docId: string, parentItemId: string) => Promise<void>) | null;
  viewerDecisionRole: AskWhom;
  disabledActions?: ItemAction[];
}

export const QuoteItemActions = ({
  canEdit,
  isManualSave,
  item,
  quote,
  project,
  onSetContentRefetchQueries,
  pathToDocPage,
  viewMode,
  setViewMode,
  documentItemTypes,
  expandedItemIds,
  pasteItem,
  viewerDecisionRole,
  disabledActions,
}: Props) => {
  const { t } = useTranslate("QuoteItem");
  const navigate = useNavigate();
  const { hasCapability, hasOrganisationPermission } = useUserData();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const projectId = project.id;
  const docId = quote.id;
  const itemId = item.id;

  const subcontractPath = getSubcontractPath(item);
  // The type rule alone is not enough to know whether the entry renders: it is
  // also gated by templating + template_document_update, and without those the divider
  // below would have nothing above it.
  const canCreateTemplate =
    !item.deletedAt &&
    rootItemsAllowedConstraints["TEMPLATE"].includes(item.type) &&
    hasCapability("templating") &&
    hasOrganisationPermission("template_document_update");
  const canCreateSubcontract =
    item.type === "section" &&
    !item.selfOrClosestSubcontract &&
    !subcontractPath;
  const canCreateProductList =
    !item.deletedAt && (item.type === "section" || item.type === "decision");
  const canHaveVerticalSelection = itemCanHaveVerticalSelection(item);
  const canBeReplacedWithTemplates = itemCanBeReplacedWithTemplates(
    item,
    documentItemTypes
  );

  const { activeWizardContext, hasItemAnyQuestions, refetchWizardQuestions } =
    useProjectQuoteContext();

  return (
    <>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: "center",
        }}
      >
        {activeWizardContext !== "item" &&
        isRefinementAskWhenContext(activeWizardContext) &&
        hasItemAnyQuestions(item.id) ? (
          <RefinementWizardButton
            projectId={projectId}
            quote={quote}
            item={item}
            askWhen={activeWizardContext}
            refetchAfterApplyItemAction={refetchWizardQuestions}
          />
        ) : null}
        {subcontractPath ? (
          <Tooltip title={t("Go to subcontracting project")}>
            <IconButton
              component={Link}
              color="primary"
              size="small"
              to={subcontractPath}
              style={{ width: 30, height: 30 }}
            >
              <FileMoveIcon style={{ width: "22px", flexShrink: 0 }} />
            </IconButton>
          </Tooltip>
        ) : undefined}
        <MenuButton
          dataTestId="quote-item-secondary-dotdotdot-menu"
          onToggle={setIsOpen}
        >
          {isManualSave && canEdit && (
            <ViewModeEditMenuItem
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
          )}
          {canCreateTemplate && (
            <RestrictedByCapabilityWithDebug capability="templating">
              <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
                <QuoteTemplateSaveFromItemMenuItem
                  projectId={projectId}
                  docId={docId}
                  sourceItemId={itemId}
                  originItemId={itemId}
                  sourceTitle={item.title}
                />
                {/* Scoped to content that came from a template *into a template
                    type slot*: a filled placeholder keeps its marker, so the
                    pair identifies the loop this action serves. A template
                    inserted anywhere else is a plain copy with no slot to keep
                    in sync, and only gets the save entry above. Which entry
                    shows — and whether this item is the sub-tree root at all —
                    comes from lookups the component runs when the menu opens. */}
                {item.templateOriginQuoteId &&
                  item.placeholderForTemplateType && (
                    <QuoteTemplateUpdateFromItemMenuItem
                      projectId={projectId}
                      docId={docId}
                      itemId={itemId}
                      parentItemId={item.parentId ?? null}
                      templateOriginQuoteId={item.templateOriginQuoteId}
                      sourceTitle={item.title}
                    />
                  )}
              </RestrictedByOrganisationPermissionWithDebug>
            </RestrictedByCapabilityWithDebug>
          )}

          {canCreateSubcontract && (
            <RestrictedByProjectPermissionWithDebug
              permission="requirement_share_create"
              project={project}
            >
              <OutsourceMenuItem
                itemId={itemId}
                docId={docId}
                projectId={projectId}
                disabled={!canEdit}
                itemTitle={item.title}
                itemDescription={item.description}
              />
            </RestrictedByProjectPermissionWithDebug>
          )}

          {canBeReplacedWithTemplates && item.parentId && (
            <RestrictedByCapabilityWithDebug capability="templating">
              <RestrictedByOrganisationPermissionWithDebug permission="template_usage_create">
                <ReplaceWithTemplatesMenuItem
                  itemId={itemId}
                  itemRank={item.rank}
                  parentItemId={item.parentId}
                  docId={docId}
                  projectId={projectId}
                  applicableFor={["QUOTE"]}
                  disabled={!canEdit}
                  onItemDeleted={() => {
                    navigate(pathToDocPage, { replace: true });
                  }}
                  startConfirmProcess={startConfirmProcess}
                  expandedItemIds={expandedItemIds}
                />
              </RestrictedByOrganisationPermissionWithDebug>
            </RestrictedByCapabilityWithDebug>
          )}

          {item.isRootItem && (
            <ExportAsXiopdMenuItem
              projectId={projectId}
              docId={docId}
              exportDestination={"UNKNOWN"}
            />
          )}
          {item.isRootItem && (
            <ExportAsXiopdMenuItem
              projectId={projectId}
              docId={docId}
              exportDestination={"MSYS"}
            />
          )}

          {canCreateProductList && (
            <RestrictedByCapabilityWithDebug capability="pim">
              <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
                <CreateProductListFromSectionMenuItem
                  projectId={projectId}
                  docId={docId}
                  itemId={itemId}
                />
              </RestrictedByOrganisationPermissionWithDebug>
            </RestrictedByCapabilityWithDebug>
          )}

          {(canCreateTemplate ||
            canCreateSubcontract ||
            canCreateProductList ||
            item.isRootItem) && <Divider />}

          <QuoteItemMenuItems
            projectId={projectId}
            docId={docId}
            item={item}
            onSetContentRefetchQueries={onSetContentRefetchQueries}
            documentItemTypes={documentItemTypes}
            expandedItemIds={expandedItemIds}
            isMenuOpen={isOpen}
            disabledActions={disabledActions}
            refetchWizardQuestions={refetchWizardQuestions}
          />
          <Divider />

          {canHaveVerticalSelection && (
            <ItemVerticalMenuItem
              projectId={projectId}
              docId={docId}
              itemId={item.id}
              vertical={item.vertical ?? null}
            />
          )}
          {canHaveVerticalSelection && <Divider />}

          <ProjectBuildingPlacementMenuItem
            projectId={projectId}
            docId={docId}
            itemId={item.id}
          />
          <Divider />

          <DocItemContextMenuItems
            pathToDocPage={pathToDocPage}
            docId={docId}
            docType="QUOTE"
            projectId={projectId}
            item={item}
            itemParentType={null}
            startConfirmProcess={startConfirmProcess}
            expandedItemIds={expandedItemIds}
            pasteItem={pasteItem}
            onAdditionalItemInputChange={null}
          />

          <Divider />

          <RunAttributeExpressionsMenuItem
            projectId={projectId}
            docId={docId}
          />
          <PropertiesDebugMenuItem
            projectId={projectId}
            docId={docId}
            itemId={itemId}
          />
        </MenuButton>
      </Stack>
      <ConfirmProcess ref={confirmProcessRef} />
    </>
  );
};
